/**
 * Trailblazers design-bundle import / export.
 *
 * Pure helpers (no React) for turning the builder's current state plus this
 * game's configs into a downloadable {@link TrlBundleFile}, and for re-hydrating
 * a chosen bundle back into builder state. Kept side-effect-light so the
 * transforms can be unit-tested; only `downloadJson` touches the DOM.
 *
 * Images and cartel rewards are referenced by library id — the file never
 * embeds binaries. On import each id is re-resolved against the target admin's
 * live library/catalogue; refs whose id is absent are dropped and reported (so
 * the graph still imports and topology/faction validation stays correct).
 */

import {
  TRL_BUNDLE_KIND,
  TRL_BUNDLE_VERSION,
  TRL_BUNDLE_SECTIONS,
  DEFAULT_TRL_NODE_SETTINGS,
  resolveTrlConsolationConfig,
  upgradeTrlStalemateConfig,
  type TrlBundleFile,
  type TrlBundleSection,
  type TrlBundleSections,
  type TrlBundleMapSection,
  type TrlBundleTreasureSection,
  type TrlMapNodeDef,
  type TrlMapEdgeDef,
  type TrlMapBackground,
  type TrlEdgeStyle,
  type TrlNodeImageRef,
  type TrlSaveMapNode,
  type TrlSaveMapEdge,
  type TrlSaveMapRequest,
  type TrlImageAsset,
  type TrlTreasureConfig,
  type TrlStalemateConfig,
  type TrlRadarConfig,
  type TrlTriangulationConfig,
  type TrlDisruptionConfig,
  type TrlSabotageConfig,
  type TrlTeamNameConfig,
} from '@swtorbit/shared-socket-events';

/** The builder's live map state, in the shape both saving and exporting need. */
export interface TrlMapState {
  nodes: TrlMapNodeDef[];
  edges: TrlMapEdgeDef[];
  background?: TrlMapBackground;
  defaultEdgeStyle?: TrlEdgeStyle;
}

/** Everything a bundle could be built from — the builder passes its live state. */
export interface TrlBundleSources extends TrlMapState {
  roomCode: string;
  canvasWidth: number;
  canvasHeight: number;
  treasure?: TrlTreasureConfig | null;
  stalemate?: TrlStalemateConfig | null;
  radar?: TrlRadarConfig | null;
  triangulation?: TrlTriangulationConfig | null;
  disruption?: TrlDisruptionConfig | null;
  sabotage?: TrlSabotageConfig | null;
  teamNames?: TrlTeamNameConfig | null;
}

/**
 * Build the sections a game could export. A section is absent when the game has
 * nothing to give for it, which is exactly what the export checklist greys out —
 * so "what can be exported" and "what gets written" come from one place.
 */
export function buildAvailableSections(src: TrlBundleSources): TrlBundleSections {
  const sections: TrlBundleSections = {};

  if (src.nodes.length > 0) sections.map = buildMapSection(src);
  if (src.treasure && src.treasure.items.length > 0) {
    sections.treasure = buildTreasureSection(src.treasure);
  }
  if (src.triangulation) sections.triangulation = { config: src.triangulation };
  if (src.stalemate) sections.stalemate = { config: src.stalemate };
  if (src.radar) sections.radar = { config: src.radar };
  if (src.disruption) sections.disruption = { config: src.disruption };
  if (src.sabotage) sections.sabotage = { config: src.sabotage };
  if (
    src.teamNames &&
    (src.teamNames.customNames.length > 0 || src.teamNames.disabledCoreNames.length > 0)
  ) {
    sections.teamNames = { config: src.teamNames };
  }

  return sections;
}

/**
 * Narrow a full section set down to the ticked ones and wrap it in a file
 * envelope. Ticking a section the game has nothing for is a no-op.
 */
export function buildBundleFile(
  available: TrlBundleSections,
  selected: ReadonlySet<TrlBundleSection>,
  roomCode: string,
  exportedAt: string,
): TrlBundleFile {
  const sections: TrlBundleSections = {};
  const copy = <K extends TrlBundleSection>(id: K) => {
    if (selected.has(id) && available[id] !== undefined) sections[id] = available[id];
  };
  for (const id of TRL_BUNDLE_SECTIONS) copy(id);

  return {
    kind: TRL_BUNDLE_KIND,
    version: TRL_BUNDLE_VERSION,
    exportedAt,
    sourceRoomCode: roomCode,
    sections,
  };
}

/**
 * The single place builder state is turned into the server's save shape.
 *
 * Both `TrlMapBuilder.doSave` and the bundle export go through here on purpose:
 * when the export kept its own copy of this mapping the two drifted, and
 * per-planet stalemate/radar overrides were silently lost on every round trip.
 */
export function buildSaveMapPayload(
  canvasWidth: number,
  canvasHeight: number,
  map: TrlMapState,
): Omit<TrlSaveMapRequest, 'roomCode'> {
  const nodes: TrlSaveMapNode[] = map.nodes.map((n) => ({
    id: n.id,
    label: n.label,
    posX: n.posX,
    posY: n.posY,
    isCheckpoint: n.isCheckpoint,
    imagesToClear: n.imagesToClear,
    imageIds: n.images.map((r) => r.imageId),
    roundType: n.roundType,
    triangulationTarget: n.triangulationTarget,
    searchDuration: n.searchDuration,
    basePoints: n.basePoints,
    pointsDecrement: n.pointsDecrement,
    stalemateOverride: n.stalemateOverride ?? null,
    radarOverride: n.radarOverride ?? null,
    icon: n.icon ?? null,
    iconColor: n.iconColor ?? null,
    treasureItemIds: n.treasureItemIds,
    decoyImageId: n.decoyImage?.imageId ?? null,
  }));

  const edges: TrlSaveMapEdge[] = map.edges.map((e) => ({
    id: e.id,
    fromNodeId: e.fromNodeId,
    toNodeId: e.toNodeId,
    controlPoints: e.controlPoints,
    style: e.style,
  }));

  return {
    canvasWidth,
    canvasHeight,
    nodes,
    edges,
    background: map.background,
    defaultEdgeStyle: map.defaultEdgeStyle,
  };
}

/** The save payload addressed to a game. See {@link buildSaveMapPayload}. */
export function buildSaveMapRequest(
  roomCode: string,
  canvasWidth: number,
  canvasHeight: number,
  map: TrlMapState,
): TrlSaveMapRequest {
  return { roomCode, ...buildSaveMapPayload(canvasWidth, canvasHeight, map) };
}

/**
 * The map section is the save payload plus the image names the importer needs
 * to report anything the target library is missing.
 */
function buildMapSection(src: TrlBundleSources): TrlBundleMapSection {
  const imageNames: Record<string, string> = {};
  for (const n of src.nodes) {
    for (const ref of n.images) {
      if (ref.imageName) imageNames[ref.imageId] = ref.imageName;
    }
    if (n.decoyImage?.imageName) imageNames[n.decoyImage.imageId] = n.decoyImage.imageName;
  }

  return { ...buildSaveMapPayload(src.canvasWidth, src.canvasHeight, src), imageNames };
}

/**
 * Cartel rewards reference platform items (`itemId`), which are the same on every
 * install, so the pool travels as it is.
 */
function buildTreasureSection(config: TrlTreasureConfig): TrlBundleTreasureSection {
  return { config };
}

/** Trigger a browser download of a bundle as pretty-printed JSON. */
export function downloadJson(file: TrlBundleFile, filename: string): void {
  const json = JSON.stringify(file, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Parse + validate raw file text into a {@link TrlBundleFile}. Throws an `Error`
 * with a user-facing message when the text isn't a valid bundle.
 *
 * Only the envelope is checked here. Each section's contents are sanitised
 * server-side by the `normalizeTrl*Config` the matching socket handler already
 * runs, so there is no second, drifting copy of those rules on the client.
 */
export function parseBundleFile(text: string): TrlBundleFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('That file is not valid JSON.');
  }

  const file = parsed as Partial<TrlBundleFile> | null;
  if (!file || typeof file !== 'object' || file.kind !== TRL_BUNDLE_KIND) {
    throw new Error('That file is not a Trailblazers design bundle.');
  }

  const version = Number(file.version);
  if (!Number.isInteger(version) || version < 1 || version > TRL_BUNDLE_VERSION) {
    throw new Error(`Unsupported bundle version (${String(file.version)}).`);
  }

  const sections = file.sections;
  if (!sections || typeof sections !== 'object') {
    throw new Error('The bundle is missing its sections.');
  }
  if (listBundleSections(sections).length === 0) {
    throw new Error('The bundle contains nothing that can be imported.');
  }
  if (sections.map && (!Array.isArray(sections.map.nodes) || !Array.isArray(sections.map.edges))) {
    throw new Error("The bundle's galaxy map is missing its planets or paths.");
  }

  // Bundles exported before the compass clue was retired carry `compass`
  // stalemate settings; they carry over to the `area` clue in its slot.
  if (sections.stalemate?.config) {
    sections.stalemate.config = upgradeTrlStalemateConfig(sections.stalemate.config) as TrlStalemateConfig;
  }
  for (const node of sections.map?.nodes ?? []) {
    if (node.stalemateOverride) {
      node.stalemateOverride = upgradeTrlStalemateConfig(node.stalemateOverride) as TrlStalemateConfig;
    }
  }

  return file as TrlBundleFile;
}

/**
 * The sections a bundle actually carries, in display order. This is the import
 * menu — unknown keys written by a newer build are ignored rather than offered.
 */
export function listBundleSections(sections: TrlBundleSections): TrlBundleSection[] {
  return TRL_BUNDLE_SECTIONS.filter((id) => sections[id] !== undefined);
}

/**
 * A resolved import: everything the ticked sections will do to this game, with
 * the losses already counted.
 *
 * Built once, by {@link buildImportPlan}, and used both to render the preview
 * and to perform the import — so what the admin was shown and what lands are
 * the same data, not two runs that could disagree.
 */
export interface TrlImportPlan {
  /** Fresh builder state for the map, or null when the map isn't being imported. */
  map: HydratedBundleMap | null;
  /** The pool to write, or null when the treasure list isn't being imported. */
  treasure: TrlTreasureConfig | null;
  /** Ticked config sections, ready to hand to their socket events. */
  configs: {
    triangulation?: TrlTriangulationConfig;
    stalemate?: TrlStalemateConfig;
    radar?: TrlRadarConfig;
    disruption?: TrlDisruptionConfig;
    sabotage?: TrlSabotageConfig;
    teamNames?: TrlTeamNameConfig;
  };
  /** Images the target library doesn't have, by name where known. */
  skippedImages: string[];
  /** Cartel rewards from a version 1 bundle (no platform item), by name where known. */
  skippedCartelItems: string[];
  /** Planets whose treasure placement is dropped (pool missing or not imported). */
  droppedTreasurePlacements: number;
}

/**
 * Resolve a bundle plus a section selection into the concrete work to be done.
 *
 * Treasure is hydrated first because the map's per-planet placements are kept
 * only for rewards that survive — and when the Treasure List isn't ticked,
 * nothing survives, so the map lands with its placements cleared rather than
 * pointing at rewards this game has never heard of.
 */
export function buildImportPlan(
  file: TrlBundleFile,
  selected: ReadonlySet<TrlBundleSection>,
  libraryImages: TrlImageAsset[],
): TrlImportPlan {
  const { sections } = file;

  const treasure =
    selected.has('treasure') && sections.treasure
      ? hydrateBundleTreasure(sections.treasure)
      : null;

  const survivingItemIds = new Set(treasure?.config.items.map((i) => i.id) ?? []);

  const map =
    selected.has('map') && sections.map
      ? hydrateBundleMap(sections.map, libraryImages, survivingItemIds)
      : null;

  const configs: TrlImportPlan['configs'] = {};
  if (selected.has('triangulation') && sections.triangulation) {
    configs.triangulation = sections.triangulation.config;
  }
  if (selected.has('stalemate') && sections.stalemate) configs.stalemate = sections.stalemate.config;
  if (selected.has('radar') && sections.radar) configs.radar = sections.radar.config;
  if (selected.has('disruption') && sections.disruption) {
    configs.disruption = sections.disruption.config;
  }
  if (selected.has('sabotage') && sections.sabotage) configs.sabotage = sections.sabotage.config;
  if (selected.has('teamNames') && sections.teamNames) configs.teamNames = sections.teamNames.config;

  return {
    map,
    treasure: treasure?.config ?? null,
    configs,
    skippedImages: map?.skippedImages ?? [],
    skippedCartelItems: treasure?.skippedCartelItems ?? [],
    droppedTreasurePlacements: map?.droppedTreasurePlacements ?? 0,
  };
}

/** Result of turning a bundle's treasure section into an importable pool. */
export interface HydratedBundleTreasure {
  config: TrlTreasureConfig;
  /** Names (or ids) of cartel rewards dropped because they have no platform item. */
  skippedCartelItems: string[];
}

/**
 * Prepare a bundle's treasure pool for import. Cartel rewards that reference a
 * platform item (`itemId`, bundle version 2) import as they are. Rewards that
 * only carry a version 1 bundle's `cartelItemId` point into the source install's
 * retired Cartel catalogue, which means nothing here, so they are dropped and
 * named for the admin to re-add.
 *
 * Covers both pools — the placed list and the consolation list — since either can name a
 * cartel item, and a dangling reference in the consolation pool would be just as broken.
 */
export function hydrateBundleTreasure(section: TrlBundleTreasureSection): HydratedBundleTreasure {
  const skippedCartelItems: string[] = [];

  /** True to keep the entry; records the name of a cartel reward with no platform item. */
  const isResolvable = (item: {
    kind: string;
    itemId?: string;
    cartelItemId?: string;
    cartelItemName?: string;
  }): boolean => {
    if (item.kind !== 'cartel' || item.itemId) return true;
    const id = item.cartelItemId ?? '';
    skippedCartelItems.push(section.cartelItemNames?.[id] ?? item.cartelItemName ?? (id || 'unnamed item'));
    return false;
  };

  const items = section.config.items.filter(isResolvable);
  const consolation = resolveTrlConsolationConfig(section.config);
  const consolationItems = consolation.items.filter(isResolvable);

  return {
    config: {
      ...section.config,
      items,
      consolation: { ...consolation, items: consolationItems },
    },
    skippedCartelItems,
  };
}

/** Result of turning a bundle's map section into builder state. */
export interface HydratedBundleMap {
  nodes: TrlMapNodeDef[];
  edges: TrlMapEdgeDef[];
  /** Imported map backdrop, or undefined if the section had none. */
  background?: TrlMapBackground;
  /** Imported map-wide default path style, or undefined if none was authored. */
  defaultEdgeStyle?: TrlEdgeStyle;
  /** Display names (or ids) of image refs dropped because they aren't in the library. */
  skippedImages: string[];
  /** How many planets lost their treasure placement because the item is gone. */
  droppedTreasurePlacements: number;
}

/**
 * Turn a bundle's map section into fresh builder state.
 *
 * - All ids are regenerated as `tmp:*` so the target game persists everything as
 *   new rows (and edge endpoints are remapped consistently to the new node ids).
 * - Each image ref is re-resolved against `libraryImages`; refs whose `imageId`
 *   isn't in the library are dropped and named in `skippedImages`.
 * - Per-planet treasure placements are kept only for ids in `survivingItemIds` —
 *   pass the imported pool's ids when the Treasure List is being imported too,
 *   and an empty set when it isn't, so the map never lands with placements
 *   pointing at rewards this game doesn't have.
 * - Cached topology fields (`isStart`/`isFinal`/`derivedLevel`) are left for the
 *   builder to recompute live.
 */
export function hydrateBundleMap(
  section: TrlBundleMapSection,
  libraryImages: TrlImageAsset[],
  survivingItemIds: ReadonlySet<string>,
): HydratedBundleMap {
  const lib = new Map(libraryImages.map((i) => [i.id, i]));
  let counter = 0;
  const nextId = (prefix: string) => `tmp:${prefix}:${(counter += 1)}`;

  // old node id -> new tmp node id (edges reference nodes by id)
  const nodeIdMap = new Map<string, string>();
  for (const n of section.nodes) nodeIdMap.set(n.id, nextId('n'));

  const skippedImages: string[] = [];
  let droppedTreasurePlacements = 0;

  const nodes: TrlMapNodeDef[] = section.nodes.map((n) => {
    const images: TrlNodeImageRef[] = [];
    (n.imageIds ?? []).forEach((imageId) => {
      const li = lib.get(imageId);
      if (!li) {
        skippedImages.push(section.imageNames?.[imageId] ?? imageId);
        return;
      }
      images.push({
        id: nextId('i'),
        imageId,
        sortOrder: images.length,
        imageUrl: li.url,
        thumbnailUrl: li.thumbUrl,
        imageName: li.name,
        imageArea: li.area,
        logAreaId: li.logAreaId,
        faction: li.faction,
      });
    });

    // The Con Clue fake resolves against the library like any other image.
    let decoyImage: TrlNodeImageRef | undefined;
    if (n.decoyImageId) {
      const li = lib.get(n.decoyImageId);
      if (li) {
        decoyImage = {
          id: nextId('d'),
          imageId: n.decoyImageId,
          sortOrder: -1,
          imageUrl: li.url,
          thumbnailUrl: li.thumbUrl,
          imageName: li.name,
          imageArea: li.area,
          logAreaId: li.logAreaId,
          faction: li.faction,
        };
      } else {
        skippedImages.push(section.imageNames?.[n.decoyImageId] ?? n.decoyImageId);
      }
    }

    const placements = n.treasureItemIds?.filter((id) => survivingItemIds.has(id));
    if ((n.treasureItemIds?.length ?? 0) > 0 && (placements?.length ?? 0) === 0) {
      droppedTreasurePlacements += 1;
    }

    return {
      id: nodeIdMap.get(n.id)!,
      label: n.label,
      posX: n.posX,
      posY: n.posY,
      isCheckpoint: n.isCheckpoint,
      isStart: false,
      isFinal: false,
      imagesToClear: n.imagesToClear ?? DEFAULT_TRL_NODE_SETTINGS.imagesToClear,
      images,
      roundType: n.roundType ?? DEFAULT_TRL_NODE_SETTINGS.roundType,
      triangulationTarget: n.triangulationTarget,
      searchDuration: n.searchDuration,
      basePoints: n.basePoints ?? DEFAULT_TRL_NODE_SETTINGS.basePoints,
      pointsDecrement: n.pointsDecrement ?? DEFAULT_TRL_NODE_SETTINGS.pointsDecrement,
      stalemateOverride: n.stalemateOverride ?? null,
      radarOverride: n.radarOverride ?? null,
      icon: n.icon ?? null,
      iconColor: n.iconColor ?? null,
      treasureItemIds: placements && placements.length > 0 ? placements : undefined,
      decoyImage,
    };
  });

  // Remap edges to the new node ids; drop any whose endpoints didn't survive.
  const edges: TrlMapEdgeDef[] = [];
  for (const e of section.edges) {
    const from = nodeIdMap.get(e.fromNodeId);
    const to = nodeIdMap.get(e.toNodeId);
    if (!from || !to) continue;
    edges.push({
      id: nextId('e'),
      fromNodeId: from,
      toNodeId: to,
      controlPoints: e.controlPoints ?? [],
      style: e.style,
    });
  }

  return {
    nodes,
    edges,
    background: section.background,
    defaultEdgeStyle: section.defaultEdgeStyle,
    skippedImages,
    droppedTreasurePlacements,
  };
}
